import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DamageProfile, NpcShip, NpcWave, shipDps, shipVolley } from '../api/sites';
import { C, FONT, glow, hexToRgba, lcarsLabelSx, prefersReducedMotion } from '../theme/tokens';

const nf = new Intl.NumberFormat('en-US');
/** 147,714 -> "148k", 1,766,775 -> "1.8m". Wave EHP totals run to seven
 * figures and the column has to stay one line at 320px; the exact number
 * lives in the tooltip. */
const compact = (n: number): string =>
  n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m` : n >= 10_000 ? `${Math.round(n / 1000)}k` : nf.format(Math.round(n));

/**
 * Sleeper waves as a real table.
 *
 * This replaces a single run-on sentence — every wave, ship, hull, EWAR tag
 * and trigger packed into one paragraph you had to parse with your eyes while
 * deciding whether to warp in. The table exists to answer three questions at a
 * glance, in the order a pilot asks them:
 *
 *   1. How many waves am I signing up for?      (the wave rail, numbered)
 *   2. What is in each one, and how big?        (rows, hull-coloured, counted)
 *   3. What kills me / what spawns the next?    (EWAR tags, TRIGGER mark)
 *
 * The delight is functional, not decorative: waves stagger in top-to-bottom so
 * the sequence reads as a sequence, and the trigger row carries a slow pulse
 * because it is the one row whose ship you must kill last, not first. Every
 * animation is one-shot on mount and honours prefers-reduced-motion.
 */

/** Hull determines the row's weight class colour — the fastest read on the
 * table. Battleships are the thing that ruins a C5 escalation; frigates are
 * noise by comparison. Towers are stationary and get the quiet neutral. */
const HULL_COLOR: Record<NpcShip['hull'], string> = {
  tower: C.neutral,
  frigate: C.cyan,
  destroyer: C.cyan,
  cruiser: C.gold,
  battlecruiser: C.gold,
  battleship: C.red,
  unknown: C.textDim,
};

/** Short label for the hull column. EVE players read "BS" faster than
 * "battleship", and it keeps the column from dominating the row. */
const HULL_ABBR: Record<NpcShip['hull'], string> = {
  tower: 'TWR',
  frigate: 'FRG',
  destroyer: 'DST',
  cruiser: 'CRU',
  battlecruiser: 'BC',
  battleship: 'BS',
  unknown: '—',
};

/* The strength bar used to be sized by a coarse hull weighting, with its own
 * comment apologising that it was "not a DPS model". It is now sized by the
 * wave's REAL incoming DPS, off the wiki's own Sleeper stat table — so the
 * bar and the number beside it are the same fact, and the hull weighting is
 * gone rather than kept as a second, worse answer. */

const NpcWaveTable: React.FC<{ waves: NpcWave[] }> = ({ waves }) => {
  const reduced = prefersReducedMotion();
  // Bars are relative to the heaviest wave in THIS site, so the shape of the
  // escalation reads regardless of whether it's a C1 or a C6.
  const heaviest = Math.max(1, ...waves.map((w) => w.totals.dps));

  return (
    <Stack spacing={1.25}>
      {waves.map((wave, i) => (
        <WaveBlock key={`${wave.label}-${i}`} wave={wave} index={i} heaviest={heaviest} reduced={reduced} />
      ))}
    </Stack>
  );
};

const WaveBlock: React.FC<{ wave: NpcWave; index: number; heaviest: number; reduced: boolean }> = ({
  wave,
  index,
  heaviest,
  reduced,
}) => {
  // An escalation is opt-in content — it must not read as a wave you are
  // guaranteed to face, so it gets the gold "optional" treatment rather than
  // sitting in the same numbered sequence.
  const accent = wave.isEscalation ? C.gold : C.wormhole;
  const strength = wave.totals.dps / heaviest;
  const count = wave.totalMin === wave.totalMax ? `${wave.totalMax}` : `${wave.totalMin}-${wave.totalMax}`;

  return (
    <Box
      sx={{
        position: 'relative',
        pl: 2,
        // The wave rail: a vertical line down the left of the whole block,
        // which is what makes a wave read as one unit rather than as loose
        // rows. It draws downward on mount — the sequence arriving in order.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 2,
          bottom: 2,
          width: 3,
          borderRadius: 999,
          backgroundColor: hexToRgba(accent, 0.55),
          transformOrigin: 'top',
          animation: reduced ? 'none' : `waveRail 0.45s cubic-bezier(0.16,1,0.3,1) ${index * 110}ms both`,
        },
        '@keyframes waveRail': {
          '0%': { transform: 'scaleY(0)', opacity: 0 },
          '100%': { transform: 'scaleY(1)', opacity: 1 },
        },
        animation: reduced ? 'none' : `waveIn 0.45s cubic-bezier(0.16,1,0.3,1) ${index * 110}ms both`,
        '@keyframes waveIn': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
        <Typography sx={{ ...lcarsLabelSx(accent, '0.74rem') }}>{wave.label}</Typography>
        <Typography sx={{ fontFamily: FONT.sub, fontWeight: 500, fontSize: '0.8rem', color: C.textDim }}>
          {count} ship{wave.totalMax === 1 ? '' : 's'}
        </Typography>
        {wave.isEscalation && (
          <Typography sx={{ ...lcarsLabelSx(C.gold, '0.6rem') }}>optional</Typography>
        )}
        {/* What this wave does to you and what it costs to kill. On the
            heading rather than in a footer row: it is the reason to read
            (or skip) the rows underneath, so it has to come first. */}
        <WaveTotals wave={wave} accent={accent} />
        <Box sx={{ flex: 1, minWidth: 40 }} />
        {/* The bar alone made you eyeball two lengths against each other to
            answer "is this the wave that kills me". The number says it. */}
        <Tooltip
          title={
            wave.totals.dps === heaviest
              ? `${nf.format(Math.round(wave.totals.dps))} DPS — the site's heaviest wave, the one your tank is sized against`
              : `${nf.format(Math.round(wave.totals.dps))} DPS — ${Math.round(strength * 100)}% of the site's heaviest wave (${nf.format(Math.round(heaviest))} DPS)`
          }
        >
          <Typography
            sx={{
              fontFamily: FONT.mono,
              fontSize: '0.66rem',
              color: wave.totals.dps === heaviest ? accent : C.textDim,
              cursor: 'help',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {Math.round(strength * 100)}% of peak
          </Typography>
        </Tooltip>
        <Tooltip
          title={`${nf.format(Math.round(wave.totals.dps))} DPS — this wave's share of the site's heaviest wave`}
        >
          <Box
            sx={{
              width: { xs: 68, md: 96 },
              height: 5,
              borderRadius: 999,
              bgcolor: hexToRgba(accent, 0.16),
              border: `1px solid ${hexToRgba(accent, 0.25)}`,
              overflow: 'hidden',
              flexShrink: 0,
              cursor: 'help',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${Math.max(6, strength * 100)}%`,
                borderRadius: 999,
                bgcolor: accent,
                boxShadow: glow(accent, 6, 0.6),
                transformOrigin: 'left',
                animation: reduced ? 'none' : `waveBar 0.6s cubic-bezier(0.16,1,0.3,1) ${index * 110 + 200}ms both`,
                '@keyframes waveBar': {
                  '0%': { transform: 'scaleX(0)' },
                  '100%': { transform: 'scaleX(1)' },
                },
              }}
            />
          </Box>
        </Tooltip>
      </Stack>

      {/* What THIS wave hits with. The site-level split lives in At a
          Glance, but a tank is set per wave: the tower-and-battleship opener
          and the frigate swarm behind it are not the same damage, and the
          site average is a number neither of them deals. */}
      {wave.damageProfile && <WaveDamageMix profile={wave.damageProfile} />}

      {/* Wave notes are prose ("70-90 km away", "spawns roughly 20 minutes
          after activation") — body voice, not the data face. */}
      {wave.note && (
        <Typography sx={{ color: C.textDim, fontFamily: FONT.body, fontSize: '0.78rem', lineHeight: 1.45, mb: 0.75 }}>
          {wave.note}
        </Typography>
      )}

      <Stack spacing={0.25}>
        {wave.ships.map((ship, j) => (
          <ShipRow key={`${ship.name}-${j}`} ship={ship} delayMs={index * 110 + 120 + j * 45} reduced={reduced} />
        ))}
      </Stack>
    </Box>
  );
};

/** The three numbers that decide whether you warp into this wave: what it
 * puts on you per second, the worst single volley it can land, and how much
 * you have to shoot off it.
 *
 * `complete: false` means a ship in the wave had no stats entry, so every
 * figure is a floor — it gets a "≥" rather than being quietly presented as a
 * total, which would understate a wave and is exactly the kind of wrong that
 * gets someone killed. */
const WaveTotals: React.FC<{ wave: NpcWave; accent: string }> = ({ wave, accent }) => {
  if (wave.totals.dps === 0 && wave.totals.ehp === 0) return null;
  const floor = wave.totals.complete ? '' : '≥';
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
      <Tooltip title={`${floor}${nf.format(Math.round(wave.totals.dps))} DPS incoming, everything in range`}>
        <Typography sx={{ ...statSx(C.red), cursor: 'help' }}>
          {floor}
          {compact(wave.totals.dps)} <Box component="span" sx={unitSx}>dps</Box>
        </Typography>
      </Tooltip>
      <Tooltip title={`${floor}${nf.format(Math.round(wave.totals.volley))} alpha if every weapon lands on the same tick`}>
        <Typography sx={{ ...statSx(C.gold), cursor: 'help' }}>
          {floor}
          {compact(wave.totals.volley)} <Box component="span" sx={unitSx}>alpha</Box>
        </Typography>
      </Tooltip>
      <Tooltip title={`${floor}${nf.format(Math.round(wave.totals.ehp))} EHP to grind through`}>
        <Typography sx={{ ...statSx(accent), cursor: 'help' }}>
          {floor}
          {compact(wave.totals.ehp)} <Box component="span" sx={unitSx}>ehp</Box>
        </Typography>
      </Tooltip>
    </Stack>
  );
};

/** The four damage types this wave puts on you, as one proportional bar.
 *
 * A bar rather than four numbers because the decision it feeds is "which
 * hole do I plug first", which is a comparison, not four readings — and the
 * widest segment answers it without arithmetic. Sleeper turrets are
 * EM/thermal and their missiles kinetic/explosive, so the split moves with
 * which hulls a wave is made of. */
const DAMAGE_COLORS: Record<keyof DamageProfile, string> = {
  em: C.blue,
  thermal: C.red,
  kinetic: C.neutral,
  explosive: C.gold,
};
const DAMAGE_ORDER: (keyof DamageProfile)[] = ['em', 'thermal', 'kinetic', 'explosive'];

const WaveDamageMix: React.FC<{ profile: DamageProfile }> = ({ profile }) => {
  const total = DAMAGE_ORDER.reduce((sum, k) => sum + profile[k], 0);
  if (total <= 0) return null;
  const worst = DAMAGE_ORDER.reduce((a, b) => (profile[b] > profile[a] ? b : a));

  return (
    <Tooltip
      title={`Incoming damage this wave: ${DAMAGE_ORDER.map((k) => `${profile[k]}% ${k}`).join(', ')} — weighted by the DPS each hull in it actually brings`}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75, cursor: 'help', maxWidth: 420 }}>
        <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.56rem'), flexShrink: 0 }}>incoming</Typography>
        <Box sx={{ display: 'flex', flex: 1, minWidth: 0, height: 4, borderRadius: 999, overflow: 'hidden' }}>
          {DAMAGE_ORDER.filter((k) => profile[k] > 0).map((k) => (
            <Box key={k} sx={{ width: `${(profile[k] / total) * 100}%`, bgcolor: DAMAGE_COLORS[k] }} />
          ))}
        </Box>
        <Typography
          sx={{ fontFamily: FONT.mono, fontSize: '0.62rem', color: DAMAGE_COLORS[worst], flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {worst} {profile[worst]}%
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const statSx = (color: string) =>
  ({ fontFamily: FONT.mono, fontSize: '0.78rem', fontWeight: 600, color, whiteSpace: 'nowrap' }) as const;
const unitSx = { color: C.textDim, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.06em' } as const;

/** One row of the table: count · hull · name · EWAR · trigger. A real grid,
 * so counts and hull tags align down the column instead of drifting with name
 * length — the alignment is what makes this scannable at all. */
const ShipRow: React.FC<{ ship: NpcShip; delayMs: number; reduced: boolean }> = ({ ship, delayMs, reduced }) => {
  const hullColor = HULL_COLOR[ship.hull];
  const meta = [
    ship.orbit,
    ship.stats?.orbitKm ? `${ship.stats.orbitKm}km @ ${ship.stats.orbitSpeed ?? '?'}m/s` : null,
    ship.stats?.sig ? `sig ${ship.stats.sig}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      sx={{
        display: 'grid',
        /* Three columns and a stats rail, never five.
         *
         * The first version put stats AND the EWAR tags in their own columns
         * beside the name. These panels are half-page wide, so `auto auto`
         * on the right left the name column narrow enough to break
         * "Sleepless Sentinel" onto two lines and stack its orbit note into
         * a one-word-per-line ribbon.
         *
         * The name now keeps the whole flexible column and the EWAR tags sit
         * on their own full-width line underneath, where a long tag has room
         * to be read instead of room to be wrapped. */
        gridTemplateColumns: {
          xs: '34px 34px minmax(0, 1fr)',
          sm: '38px 38px minmax(0, 1fr) auto',
        },
        alignItems: 'baseline',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: '6px',
        // The trigger row is the one you must NOT shoot first — it earns a
        // tinted ground and a slow breath so the eye returns to it.
        backgroundColor: ship.isTrigger ? hexToRgba(C.red, 0.08) : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: hexToRgba(hullColor, 0.1) },
        animation: reduced ? 'none' : `shipIn 0.35s cubic-bezier(0.16,1,0.3,1) ${delayMs}ms both`,
        '@keyframes shipIn': {
          '0%': { opacity: 0, transform: 'translateX(-6px)' },
          '100%': { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text, textAlign: 'right' }}>
        {ship.count}
        <Box component="span" sx={{ color: C.textDim, fontSize: '0.7rem' }}>
          &times;
        </Box>
      </Typography>

      <Tooltip title={ship.hull === 'unknown' ? 'Hull class not recorded' : ship.hull}>
        <Typography
          sx={{
            fontFamily: FONT.mono,
            fontSize: '0.64rem',
            color: hullColor,
            textAlign: 'center',
            border: `1px solid ${hexToRgba(hullColor, 0.4)}`,
            borderRadius: 999,
            py: 0.15,
            cursor: 'help',
          }}
        >
          {HULL_ABBR[ship.hull]}
        </Typography>
      </Tooltip>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: C.text, fontSize: '0.85rem', lineHeight: 1.3 }}>{ship.name}</Typography>
        {/* Orbit distance and speed decide whether your guns track it and
            whether it sits inside your neut range, so the catalogue's own
            note and the stat table's figures both belong on this line. */}
        {meta && (
          <Typography
            sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.64rem', lineHeight: 1.4 }}
          >
            {meta}
          </Typography>
        )}
      </Box>

      {/* Per-hull stats. `singular / group` answers both questions at once —
          "what does ONE of these do" (which decides your fit) and "what does
          this group do" (which decides the wave). */}
      <ShipStats ship={ship} />

      {(ship.ewar.length > 0 || ship.isTrigger) && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{
            // Always its own full-width line under the name. A long tag
            // ("stasis webifier/warp disruptor/energy neutralization") needs
            // the whole panel to stay one readable string; sharing the row
            // is what forced it to wrap mid-phrase and crushed the name.
            gridColumn: '1 / -1',
            pl: { xs: '38px', sm: '84px' },
            mt: 0.25,
            // A grid item stretches to its column by default, so the row is
            // already exactly as wide as it may be. Both a `width: 100%` and
            // a `maxWidth: 100%` here resolve against the column BEFORE the
            // indent below is added, which re-adds the padding and pushes
            // the document a couple of pixels past a phone viewport.
            minWidth: 0,
          }}
        >
          {ship.ewar.map((e) => (
            <EwarTag key={e} label={e} />
          ))}
          {ship.isTrigger && <TriggerTag reduced={reduced} />}
        </Stack>
      )}
    </Box>
  );
};

/** What one of these hulls does, and what the whole group of them does.
 *
 * Both numbers, always, because they answer different questions: one hull's
 * DPS is what your resist profile is sized against, while the group's is
 * what your tank is. Showing only the group total is how "4x Sleepless
 * Sentinel" reads as a single unkillable thing; showing only the singular is
 * how a wave of nine frigates reads as harmless.
 *
 * A hull the stat table doesn't know renders nothing at all rather than a
 * zero — the wave header's "≥" is what tells you something is missing. */
const ShipStats: React.FC<{ ship: NpcShip }> = ({ ship }) => {
  const stats = ship.stats;
  if (!stats) return <Box sx={{ display: { xs: 'none', sm: 'block' } }} />;

  const each = { dps: shipDps(stats), volley: shipVolley(stats), ehp: stats.ehp ?? 0 };
  const group = {
    dps: each.dps * ship.countMax,
    volley: each.volley * ship.countMax,
    ehp: each.ehp * ship.countMax,
  };
  const many = ship.countMax > 1;

  const cell = (label: string, color: string, one: number, all: number, hint: string) => (
    <Tooltip
      title={
        many
          ? `${nf.format(one)} ${label} each · ${nf.format(all)} for all ${ship.countMax} — ${hint}`
          : `${nf.format(one)} ${label} — ${hint}`
      }
    >
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color, cursor: 'help', whiteSpace: 'nowrap', textAlign: 'right' }}>
        {compact(one)}
        {many && (
          <Box component="span" sx={{ color: C.textDim }}>
            {' '}
            /{compact(all)}
          </Box>
        )}
      </Typography>
    </Tooltip>
  );

  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        gridColumn: { xs: '1 / -1', sm: 'auto' },
        pl: { xs: '38px', sm: 0 },
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        flexShrink: 0,
      }}
    >
      {cell('DPS', C.red, each.dps, group.dps, 'turret + missile together')}
      {cell('alpha', C.gold, each.volley, group.volley, 'both weapons on the same tick')}
      {cell('EHP', C.cyan, each.ehp, group.ehp, resistHint(stats))}
    </Stack>
  );
};

/** What the EHP figure hides.
 *
 * Sleepers are famously omni-tanked, and the dogma bears that out: 29 of the
 * 32 hulls in the catalogue take identical EHP from all four damage types,
 * so a per-type column would be four copies of the same number on almost
 * every row — noise, not information. The three that differ (the Sleepless
 * Guardian family, harder against thermal) are worth saying out loud,
 * because "shoot it with anything" stops being true on exactly those rows. */
function resistHint(stats: { ehp: number; ehpByType: DamageProfile }): string {
  const base = 'what you have to shoot off it';
  const values = DAMAGE_ORDER.map((k) => stats.ehpByType[k]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min <= 0 || max / min < 1.05) return `${base} — flat resists, any damage type`;
  const toughest = DAMAGE_ORDER.reduce((a, b) => (stats.ehpByType[b] > stats.ehpByType[a] ? b : a));
  return `${base} — resists ${toughest} harder (${nf.format(Math.round(max))} EHP vs ${toughest}, ${nf.format(Math.round(min))} against the rest)`;
}

/** EWAR is the thing that decides whether your fit works here, so it gets a
 * real tag rather than parenthetical prose. */
const EwarTag: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      px: 0.75,
      py: 0.1,
      minWidth: 0,
      borderRadius: 999,
      border: `1px solid ${hexToRgba(C.gold, 0.4)}`,
      bgcolor: hexToRgba(C.gold, 0.07),
    }}
  >
    {/* The corpus has EWAR strings up to "stasis webifier/warp disruptor/
        energy neutralization" — 293px on its own, which pushed the mobile
        document 32px past the viewport when held on one line. It wraps at the
        slashes instead; nowrap here is what broke the page width. */}
    <Typography
      sx={{
        fontFamily: FONT.mono,
        fontSize: '0.62rem',
        color: C.gold,
        lineHeight: 1.35,
        overflowWrap: 'anywhere',
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** The one mark on this table that pulses. It is the site's actual mechanic:
 * kill this ship and the next wave lands on you. */
const TriggerTag: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <Tooltip title="Killing this ship spawns the next wave">
    <Box
      sx={{
        px: 0.75,
        py: 0.1,
        borderRadius: 999,
        border: `1px solid ${hexToRgba(C.red, 0.6)}`,
        bgcolor: hexToRgba(C.red, 0.12),
        cursor: 'help',
        animation: reduced ? 'none' : 'triggerPulse 2.8s ease-in-out infinite',
        '@keyframes triggerPulse': {
          '0%, 100%': { boxShadow: glow(C.red, 0, 0) },
          '50%': { boxShadow: glow(C.red, 10, 0.5) },
        },
      }}
    >
      <Typography
        sx={{ fontFamily: FONT.mono, fontSize: '0.62rem', color: C.red, fontWeight: 700, letterSpacing: '0.08em' }}
      >
        TRIGGER
      </Typography>
    </Box>
  </Tooltip>
);

export default NpcWaveTable;
